import { useCallback, useEffect, useRef, useState } from 'react';
import { getGPSHistory } from '../../Api/LiveTrackingData';
import { GetHistoricalBunker } from '../../Api/getData';
import { formatRefDate } from '../../utils/formatRefDate';

const idle = () => ({ status: 'idle', data: null, error: '' });
const loading = () => ({ status: 'loading', data: null, error: '' });
const bunkerKeys = ['BeginingStock', 'EndingStock', 'BBM_IN', 'BBM_OUT'];

function readGPS(response, filter) {
    if (!response || response.source !== 'saved-gps-reports' || !Array.isArray(response.points)
        || response.vessel?.accountId !== filter.accountId || response.vessel?.objectCode !== filter.objectCode
        || response.period?.start !== filter.start || response.period?.end !== filter.end
        || response.points.some(point => !Number.isFinite(point.latitude) || !Number.isFinite(point.longitude)
            || Math.abs(point.latitude) > 90 || Math.abs(point.longitude) > 180
            || formatRefDate(point.refDate) === '—' || !Number.isInteger(point.segment) || point.segment < 0)) {
        throw new Error('The GPS history response is invalid. Please retry.');
    }
    return response;
}

function readBunker(response) {
    if (!Array.isArray(response)) throw new Error('Bunker history could not be loaded. Please retry.');
    if (!response.length) return null;
    const row = response[0];
    if (!row || typeof row !== 'object' || Object.keys(row).some(key => /error/i.test(key))
        || !bunkerKeys.some(key => Object.hasOwn(row, key))) throw new Error('Bunker history could not be loaded. Please retry.');
    return row;
}

export default function useTrackingHistory() {
    const [applied, setApplied] = useState(null);
    const [gps, setGPS] = useState(idle);
    const [bunker, setBunker] = useState(idle);
    const generation = useRef(0);
    const controllers = useRef({});
    const snapshot = useRef(null);
    const applying = useRef(false);

    const cancel = useCallback(() => {
        generation.current++;
        Object.values(controllers.current).forEach(controller => controller.abort());
        controllers.current = {};
        applying.current = false;
    }, []);
    useEffect(() => cancel, [cancel]);

    const load = useCallback(async (section, filter, token) => {
        const controller = new AbortController();
        controllers.current[section]?.abort();
        controllers.current[section] = controller;
        const setResult = section === 'gps' ? setGPS : setBunker;
        setResult(loading());
        try {
            const response = section === 'gps'
                ? await getGPSHistory({ accountId: filter.accountId, objectCode: filter.objectCode, start: filter.start, end: filter.end }, controller.signal)
                : await GetHistoricalBunker({ AccountId: filter.accountId, ObjectCode: filter.objectCode, StartDate: filter.start, EndDate: filter.end }, { signal: controller.signal, timeout: 120000 });
            if (controller.signal.aborted || token !== generation.current) return;
            const data = section === 'gps' ? readGPS(response, filter) : readBunker(response);
            setResult({ status: 'ready', data, error: '' });
        } catch (error) {
            if (controller.signal.aborted || token !== generation.current) return;
            const message = typeof error.response?.data?.message === 'string' ? error.response.data.message
                : error.code === 'ECONNABORTED' ? 'The request timed out. Select a shorter period or retry.'
                    : error.message?.includes('history') ? error.message : `Unable to load ${section === 'gps' ? 'GPS' : 'bunker'} history. Please retry.`;
            setResult({ status: 'error', data: null, error: message });
        } finally {
            if (controllers.current[section] === controller) delete controllers.current[section];
        }
    }, []);

    const apply = useCallback(filter => {
        if (applying.current) return;
        cancel();
        applying.current = true;
        const token = generation.current;
        snapshot.current = filter;
        setApplied(filter);
        Promise.allSettled([load('gps', filter, token), load('bunker', filter, token)]).then(() => {
            if (generation.current === token) applying.current = false;
        });
    }, [cancel, load]);
    const reset = useCallback(() => {
        cancel(); snapshot.current = null;
        setApplied(null); setGPS(idle()); setBunker(idle());
    }, [cancel]);
    const retry = useCallback(section => {
        if (!snapshot.current || controllers.current[section]) return;
        load(section, snapshot.current, generation.current);
    }, [load]);
    return { applied, gps, bunker, apply, reset, retry };
}
